import {
	ChangeEvent,
	ComponentProps,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";

import { cn } from "@lib/utils";

import { DELAY_IMMEDIATE } from "@configs/constants";

export type AutoSizeType = boolean | { minRows?: number; maxRows?: number };

export interface TextareaProps extends ComponentProps<"textarea"> {
	showCount?: boolean;
	maxLength?: number;
	rows?: number;
	isResize?: boolean;
	autoSize?: AutoSizeType;
}

/** Displays a form textarea or a component that looks like a textarea.
 * @prop className - The class name to apply to the textarea
 * @prop showCount - Whether to show the count of the textarea
 * @prop maxLength - The maximum length of the textarea
 * @prop rows - The number of rows of the textarea
 * @prop value - The value of the textarea
 * @prop defaultValue - The default value of the textarea
 * @prop onFocus - The function to call when the textarea is focused
 * @prop isResize - Allow manual resizing of the textarea. When false, resize is disabled.
 * @prop autoSize - Auto adjust the height. Can be boolean or { minRows, maxRows } object.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(
		{ className, showCount, maxLength, rows, isResize = false, autoSize, value, defaultValue, onFocus, ...props },
		ref,
	) => {
		const [currentValue, setCurrentValue] = useState<string>(
			typeof value === "string" ? value : typeof defaultValue === "string" ? defaultValue : "",
		);

		const textareaRef = useRef<HTMLTextAreaElement>(null);
		useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

		const textareaValue = value !== undefined ? value : currentValue;

		const resizeTextarea = useCallback(() => {
			const textarea = textareaRef.current;
			if (!textarea || !autoSize) return;

			const computedStyle = window.getComputedStyle(textarea);
			const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.2;
			const paddingTop = parseFloat(computedStyle.paddingTop);
			const paddingBottom = parseFloat(computedStyle.paddingBottom);
			const borderTop = parseFloat(computedStyle.borderTopWidth);
			const borderBottom = parseFloat(computedStyle.borderBottomWidth);
			const extra = paddingTop + paddingBottom + borderTop + borderBottom;

			const minRows = typeof autoSize === "object" ? autoSize.minRows : undefined;
			const maxRows = typeof autoSize === "object" ? autoSize.maxRows : undefined;

			textarea.style.height = "auto";
			const scrollHeight = textarea.scrollHeight;

			let height = scrollHeight;

			if (minRows) {
				const minHeight = lineHeight * minRows + extra;
				height = Math.max(height, minHeight);
			}

			if (maxRows) {
				const maxHeight = lineHeight * maxRows + extra;
				height = Math.min(height, maxHeight);
				textarea.style.overflowY = height >= maxHeight ? "auto" : "hidden";
			} else {
				textarea.style.overflowY = "hidden";
			}

			textarea.style.height = `${height}px`;
		}, [autoSize]);

		useEffect(() => {
			resizeTextarea();
		}, [textareaValue, resizeTextarea]);

		const currentLength = typeof textareaValue === "string" ? textareaValue.length : 0;

		const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
			const textarea = textareaRef.current;
			if (textarea) {
				setTimeout(() => {
					const length = textarea.value.length;
					textarea.setSelectionRange(length, length);
				}, DELAY_IMMEDIATE);
			}
			if (onFocus) {
				onFocus(e);
			}
		};

		const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
			if (value === undefined) {
				setCurrentValue(e.target.value);
			}
			if (props.onChange) {
				props.onChange(e);
			}
		};

		const hasCount = showCount && maxLength;

		const textareaElement = (
			<textarea
				ref={textareaRef}
				data-slot="textarea"
				rows={rows}
				maxLength={maxLength}
				value={textareaValue}
				onChange={handleChange}
				onFocus={handleFocus}
				className={cn(
					`custom-scrollbar aria-invalid:ring-destructive/20 aria-invalid:border-destructive md:text-sm
					placeholder:text-muted-foreground w-full rounded-lg border border-input bg-transparent px-2.5 py-2 text-base
					text-white transition-colors outline-none disabled:cursor-not-allowed disabled:bg-glass disabled:opacity-50
					aria-invalid:ring-[3px]`,
					autoSize ? "resize-none overflow-hidden" : "overflow-y-auto",
					!autoSize && (isResize ? "resize-y" : "resize-none"),
					"hover:border-msg-time focus:border-msg-time focus-visible:border-msg-time",
					className,
				)}
				{...props}
			/>
		);

		if (hasCount) {
			return (
				<div className={cn("w-full", className)}>
					{textareaElement}
					<div className="flex justify-end text-xs text-msg-time">
						{currentLength}/{maxLength}
					</div>
				</div>
			);
		}

		return textareaElement;
	},
);

Textarea.displayName = "Textarea";
